/**
 * Analytics service for tracking shade builder events in GA4
 * Integrates with Shopify's existing GA4 installation
 */

// Safe wrapper for Shopify's gtag
export const trackEvent = (eventName, parameters = {}) => {
  // Check if gtag exists (from Shopify)
  if (typeof window.gtag !== 'function') {
    console.warn('GA4 not available - event not tracked:', eventName);
    return;
  }
  
  try {
    // Add common parameters
    const enrichedParams = {
      ...parameters,
      custom_app: 'shade_builder',
      shop_domain: window.Shopify?.shop || 'unknown',
      timestamp: new Date().toISOString()
    };
    
    window.gtag('event', eventName, enrichedParams);
    
    // Log in development
    if (window.location.hostname === 'localhost') {
      console.log('GA4 Event:', eventName, enrichedParams);
    }
  } catch (error) {
    console.error('GA4 tracking error:', error);
  }
};

// Track shade builder initialization
export const trackShadeBuilderStart = (hasUrlParams = false, restoredState = null) => {
  trackEvent('shade_builder_started', {
    has_url_params: hasUrlParams,
    restored_from_url: Boolean(restoredState?.selectedType),
    event_category: 'shade_builder',
    event_label: 'session_start'
  });
};

// Track step completions
export const trackStepCompletion = (stepNumber, stepData) => {
  const stepNames = {
    1: 'type_selection',
    2: 'size_configuration', 
    3: 'color_selection',
    4: 'order_confirmation'
  };
  
  trackEvent(`shade_builder_step_${stepNumber}_completed`, {
    ...stepData,
    step_name: stepNames[stepNumber],
    event_category: 'shade_builder',
    event_label: `step_${stepNumber}`
  });
};

// Track configuration changes
export const trackConfigurationChange = (field, oldValue, newValue, currentStep) => {
  trackEvent('shade_configuration_changed', {
    field_name: field,
    old_value: oldValue || 'none',
    new_value: newValue,
    current_step: currentStep,
    event_category: 'shade_builder',
    event_label: field
  });
};

// Track price calculation
export const trackPriceCalculated = (priceData) => {
  trackEvent('shade_price_calculated', {
    base_price: priceData.basePrice,
    rush_fee: priceData.rushFee || 0,
    total_price: priceData.totalPrice,
    shade_type: priceData.shadeType,
    configuration: priceData.configuration,
    width: priceData.width,
    height: priceData.height,
    event_category: 'shade_builder',
    event_value: priceData.totalPrice
  });
};

// Track add to cart - using Shopify's ecommerce event format
export const trackAddToCart = (configuration, price, quantity = 1) => {
  const itemData = {
    item_id: configuration.variantId || 'custom_shade',
    item_name: `${configuration.shadeType} ${configuration.configuration} Shade`,
    price: parseFloat(price),
    quantity: quantity,
    item_category: 'Window Shades',
    item_category2: configuration.shadeType,
    item_variant: configuration.configuration,
    // Custom parameters
    shade_width: configuration.width,
    shade_height: configuration.height,
    top_rail_color: configuration.colors.topRail,
    night_shade_color: configuration.colors.nightShade,
    day_shade_color: configuration.colors.dayShade || 'none',
    rush_order: configuration.rushOrder
  };
  
  // Standard Shopify add_to_cart event
  trackEvent('add_to_cart', {
    currency: 'USD',
    value: parseFloat(price) * quantity,
    items: [itemData],
    event_category: 'shade_builder',
    custom_product: true
  });
  
  // Also track a custom event with more details
  trackEvent('shade_builder_add_to_cart', {
    ...itemData,
    total_value: parseFloat(price) * quantity,
    event_category: 'shade_builder'
  });
};

// Track user going back to previous steps
export const trackStepRevisited = (fromStep, toStep) => {
  trackEvent('shade_builder_step_revisited', {
    from_step: fromStep,
    to_step: toStep,
    direction: fromStep > toStep ? 'backward' : 'forward',
    event_category: 'shade_builder',
    event_label: `step_${fromStep}_to_${toStep}`
  });
};

// Track errors
export const trackError = (errorType, errorMessage, step) => {
  trackEvent('shade_builder_error', {
    error_type: errorType,
    error_message: errorMessage,
    current_step: step,
    event_category: 'shade_builder',
    event_label: errorType
  });
};

// Helper function to categorize shade sizes
export const getSizeCategory = (dimensions) => {
  const area = dimensions.width * dimensions.height;
  if (area < 1000) return 'small';
  if (area < 2500) return 'medium';
  return 'large';
};

// Track session timing
let sessionStartTime = Date.now();
let stepStartTimes = {};

export const startStepTimer = (step) => {
  stepStartTimes[step] = Date.now();
};

export const trackStepTiming = (step) => {
  if (stepStartTimes[step]) {
    const duration = Date.now() - stepStartTimes[step];
    trackEvent('shade_builder_step_timing', {
      step_number: step,
      duration_seconds: Math.round(duration / 1000),
      event_category: 'shade_builder',
      event_label: `step_${step}_timing`
    });
  }
};

export const trackSessionComplete = () => {
  const totalDuration = Date.now() - sessionStartTime;
  trackEvent('shade_builder_session_complete', {
    total_duration_seconds: Math.round(totalDuration / 1000),
    event_category: 'shade_builder',
    event_label: 'session_complete'
  });
};